import React, { useCallback, useEffect, useState } from 'react';
import {
    Paper, Typography, Box, Button, TextField, Grid, Switch, FormControlLabel, Divider,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { getBillingSettingsService, saveBillingSettingsService } from '../../../../services/billing.service';
import { toastSuccess } from '../../../../utils/utils';

/**
 * PaymentSettingsTab — payment options buyers see in the portal's checkout.
 *
 * - Wallet payments (Apple Pay, Google Pay, Link): off means checkout is card only.
 * - Bank transfer: offered only when turned on AND the bank details a buyer needs
 *   are filled in. The details below are exactly what the buyer is shown.
 */

const FIELDS = [
    { key: 'bank_name', note: true,       label: 'Bank name',       required: true },
    { key: 'account_name', note: true,    label: 'Account name',    required: true },
    { key: 'account_number', note: true,  label: 'Account number',  required: true },
    { key: 'swift_bic', note: true,       label: 'SWIFT / BIC',     required: true },
    { key: 'bank_code', note: true,       label: 'Bank code' },
    { key: 'bank_address', note: true,   label: 'Bank address',    multiline: true },
    { key: 'support_email',   label: 'Support email',   help: 'Shown under "Need help?", and where buyers may email proof.' },
    { key: 'support_phone',   label: 'Support phone' },
    { key: 'processing_note', label: 'Processing note', multiline: true, help: 'Shown under "Important", e.g. "Payments are usually reflected within 1–2 business days after we receive the funds."' },
];

// Every bank detail also has an optional note, shown beside it in brackets.
const noteKey = (key) => `${key}_note`;
const EMPTY_WIRE = FIELDS.reduce(
    (acc, f) => ({ ...acc, [f.key]: '', ...(f.note ? { [noteKey(f.key)]: '' } : {}) }),
    { enabled: false },
);

const fmtDate = (d) => (d ? new Date(d).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const PaymentSettingsTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [walletsEnabled, setWalletsEnabled] = useState(false);
    const [wire, setWire] = useState(EMPTY_WIRE);
    const [meta, setMeta] = useState({ updated_by: null, updated_at: null });

    const apply = (data) => {
        if (!data) return;
        setWalletsEnabled(!!data.wallets_enabled);
        setWire({ ...EMPTY_WIRE, ...(data.wire_transfer || {}) });
        setMeta({ updated_by: data.updated_by, updated_at: data.updated_at });
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getBillingSettingsService();
            apply(res?.data);
        } catch (err) {
            // invokeApi has already shown the error.
            console.error('[PaymentSettingsTab] load failed:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const missing = FIELDS.filter((f) => f.required && !String(wire[f.key] || '').trim()).map((f) => f.label);

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await saveBillingSettingsService({ wallets_enabled: walletsEnabled, wire_transfer: wire });
            apply(res?.data);
            toastSuccess('Payment settings saved.');
        } catch (err) {
            // invokeApi has already shown the API's reason (e.g. missing bank details).
            console.error('[PaymentSettingsTab] save failed:', err);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box p={4} display="flex" justifyContent="center"><CircularProgress/></Box>;

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
                <Paper style={{ padding: 24 }}>
                    <Typography variant="overline" color="textSecondary">Card checkout</Typography>
                    <FormControlLabel
                        style={{ display: 'flex', marginTop: 8 }}
                        control={<Switch checked={walletsEnabled} onChange={(e) => { const { checked } = e.target; setWalletsEnabled(checked); }} color="primary"/>}
                        label={<Typography style={{ fontWeight: 600 }}>Wallet payments</Typography>}
                    />
                    <Typography variant="body2" color="textSecondary">
                        Apple Pay, Google Pay and Link. When off, buyers can pay by card only.
                    </Typography>

                    <Divider style={{ margin: '20px 0' }}/>

                    <Typography variant="overline" color="textSecondary">Bank transfer</Typography>
                    <FormControlLabel
                        style={{ display: 'flex', marginTop: 8 }}
                        control={<Switch checked={!!wire.enabled} onChange={(e) => { const { checked } = e.target; setWire((w) => ({ ...w, enabled: checked })); }} color="primary"/>}
                        label={<Typography style={{ fontWeight: 600 }}>Accept bank transfers</Typography>}
                    />
                    <Typography variant="body2" color="textSecondary">
                        Buyers pay to the account on the right, quoting their invoice number, then upload proof.
                        Approve or reject transfers in the Bank Transfers tab.
                    </Typography>
                    {wire.enabled && missing.length > 0 && (
                        <Typography variant="body2" style={{ color: '#b45309', marginTop: 12 }}>
                            Fill in {missing.join(', ')} before saving — buyers cannot pay without them.
                        </Typography>
                    )}

                    <Typography variant="caption" color="textSecondary" component="p" style={{ marginTop: 20 }}>
                        Last saved {fmtDate(meta.updated_at)}{meta.updated_by?.label ? ` by ${meta.updated_by.label}` : ''}
                    </Typography>
                </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
                <Paper style={{ padding: 24 }}>
                    <Typography variant="overline" color="textSecondary">Bank details shown to buyers</Typography>
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                        {FIELDS.map((f) => (
                            <Grid item xs={12} sm={f.multiline ? 12 : 6} key={f.key}>
                                <TextField
                                    fullWidth variant="outlined" size="small"
                                    label={f.label} required={f.required && !!wire.enabled}
                                    value={wire[f.key] || ''}
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        setWire((w) => ({ ...w, [f.key]: value }));
                                    }}
                                    multiline={!!f.multiline} minRows={f.multiline ? 2 : undefined}
                                    inputProps={{ maxLength: 500 }}
                                    helperText={f.help || ' '}
                                />
                                {f.note && (
                                    <TextField
                                        fullWidth variant="outlined" size="small"
                                        style={{ marginTop: 4 }}
                                        label={`${f.label} note (optional)`}
                                        placeholder="e.g. Use when sending money from outside Singapore"
                                        value={wire[noteKey(f.key)] || ''}
                                        onChange={(e) => {
                                            const { value } = e.target;
                                            setWire((w) => ({ ...w, [noteKey(f.key)]: value }));
                                        }}
                                        inputProps={{ maxLength: 500 }}
                                        helperText="Shown in brackets beside this field."
                                    />
                                )}
                            </Grid>
                        ))}
                    </Grid>
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button variant="contained" color="primary" onClick={handleSave}
                            disabled={saving || (!!wire.enabled && missing.length > 0)}>
                            {saving ? <CircularProgress size={20} style={{ color: '#fff' }}/> : 'Save settings'}
                        </Button>
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default PaymentSettingsTab;
